import Fuse from "fuse.js";
import productWeights from "../data/product_weights.json";

export type ProductWeightEstimate = {
  name: string;
  weight_kg: {
    min: number;
    max: number;
  };
};

export type EstimatedWeightRange = {
  product: ProductWeightEstimate;
  quantity: number;
  min: number;
  max: number;
};

type ProductWeightNode = {
  name?: string;
  items?: ProductWeightNode[];
  sub_categories?: ProductWeightNode[];
  weight_kg?: {
    min: number;
    max: number;
  };
};

function collectProductWeightEstimates(nodes: ProductWeightNode[]): ProductWeightEstimate[] {
  return nodes.flatMap((node) => {
    const products: ProductWeightEstimate[] = [];

    if (node.name && node.weight_kg) {
      products.push({
        name: node.name,
        weight_kg: node.weight_kg,
      });
    }

    if (node.items) {
      products.push(...collectProductWeightEstimates(node.items));
    }

    if (node.sub_categories) {
      products.push(...collectProductWeightEstimates(node.sub_categories));
    }

    return products;
  });
}

export const productWeightEstimates = collectProductWeightEstimates(
  productWeights.weight_estimates,
);

const productFuse = new Fuse(productWeightEstimates, {
  keys: ["name"],
  threshold: 0.28,
  distance: 100,
  ignoreLocation: true,
  shouldSort: true,
  includeScore: true,
  minMatchCharLength: 2,
});

const popularProductNames = [
  "Tote bags (canvas)",
  "School bags",
  "Messenger bags",
  "Clutch bags",
  "Laptop Sleeves & Briefcases",
  "Food processor (small)",
  "Perfume (50ml)",
  "Sneakers",
];

const normalizeSearchTerm = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

function getProductRank(productName: string, searchTerm: string) {
  const normalizedName = normalizeSearchTerm(productName);
  const normalizedSearchTerm = normalizeSearchTerm(searchTerm);

  if (!normalizedSearchTerm) return null;
  if (normalizedName === normalizedSearchTerm) return 0;
  if (normalizedName.startsWith(normalizedSearchTerm)) return 1;

  const tokens = normalizedName.split(" ");
  if (tokens.includes(normalizedSearchTerm)) return 2;
  if (tokens.some((token) => token.startsWith(normalizedSearchTerm))) return 3;
  if (normalizedName.includes(normalizedSearchTerm)) return 4;

  return null;
}

export const popularProductWeightEstimates = popularProductNames
  .map((name) => productWeightEstimates.find((product) => product.name === name))
  .filter((product): product is ProductWeightEstimate => Boolean(product));

export function searchProductWeightEstimates(productType: string, limit?: number) {
  const searchTerm = productType.trim();

  if (!searchTerm) return [];

  const directMatches = productWeightEstimates
    .map((product) => ({
      product,
      rank: getProductRank(product.name, searchTerm),
    }))
    .filter((match): match is { product: ProductWeightEstimate; rank: number } => match.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.product.name.localeCompare(b.product.name))
    .map(({ product }) => product);

  const seenProducts = new Set(directMatches.map((product) => product.name));
  const fuzzyMatches = productFuse
    .search(searchTerm)
    .filter((result) => (result.score ?? 1) <= 0.28 && !seenProducts.has(result.item.name))
    .map((result) => result.item);

  const matches = [...directMatches, ...fuzzyMatches];
  return typeof limit === "number" ? matches.slice(0, limit) : matches;
}

export function findProductWeightEstimate(productType: string) {
  const searchTerm = productType.trim();

  if (!searchTerm) return null;

  return searchProductWeightEstimates(searchTerm, 1)[0] ?? null;
}

export function getEstimatedWeightRange(
  productType: string,
  quantityValue: string | number,
  selectedProduct?: ProductWeightEstimate | null,
) {
  const quantity = Number(quantityValue);

  if (!Number.isFinite(quantity) || quantity <= 0) return null;

  const product = selectedProduct ?? findProductWeightEstimate(productType);

  if (!product) return null;

  return {
    product,
    quantity,
    min: product.weight_kg.min * quantity,
    max: product.weight_kg.max * quantity,
  };
}
